// redux/slices/ui.ts
import {
  createSlice,
  PayloadAction,
  isRejected,
  isFulfilled,
} from "@reduxjs/toolkit";
import type { AlertColor } from "@mui/material";
import {
  updateInvoicesBatchSent,
  updateInvoicesBatchPaid,
  updateInvoicesBatchReminder,
} from "./invoice";

type SnackbarState = {
  open: boolean;
  message: string;
  severity: AlertColor;
  duration?: number;
};

interface UIState {
  snackbar: SnackbarState | null;
}

const initialState: UIState = {
  snackbar: null,
};

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    showSnackbar: (state, action: PayloadAction<SnackbarState>) => {
      state.snackbar = { ...action.payload, open: true };
    },
    hideSnackbar: (state) => {
      if (state.snackbar) state.snackbar.open = false;
    },
  },
  extraReducers: (builder) => {
    // Success messages for specific thunks
    builder.addCase(updateInvoicesBatchSent.fulfilled, (state, action) => {
      state.snackbar = {
        open: true,
        message: `Lähetettiin ${action.payload.ids.length} lasku${
          action.payload.ids.length === 1 ? "" : "a"
        }.`,
        severity: "success",
      };
    });
    builder.addCase(updateInvoicesBatchPaid.fulfilled, (state, action) => {
      state.snackbar = {
        open: true,
        message: `Merkitty maksetuksi: ${action.payload.ids.length} lasku(a).`,
        severity: "success",
      };
    });
    builder.addCase(updateInvoicesBatchReminder.fulfilled, (state, action) => {
      state.snackbar = {
        open: true,
        message: `Muistutus lähetetty ${action.payload.ids.length} laskulle.`,
        severity: "success",
      };
    });

    // Generic error handler for any rejected action in this feature (optional)
    builder.addMatcher(
      isRejected(
        updateInvoicesBatchSent,
        updateInvoicesBatchPaid,
        updateInvoicesBatchReminder
      ),
      (state, action: any) => {
        const msg =
          action.payload?.message ||
          action.error?.message ||
          "Toiminto epäonnistui. Yritä uudelleen.";
        state.snackbar = { open: true, message: msg, severity: "error" };
      }
    );

    // Optional: clear snackbar on new successes too
    builder.addMatcher(
      isFulfilled(
        updateInvoicesBatchSent,
        updateInvoicesBatchPaid,
        updateInvoicesBatchReminder
      ),
      (state) => {
        // no-op; success handlers above already set snackbar
      }
    );
  },
});

export const { showSnackbar, hideSnackbar } = uiSlice.actions;
export default uiSlice.reducer;
